import type { Custodian } from 'models/custodian';
import type { MapStateToPropsFactory } from 'react-redux';
import { connect } from 'react-redux';
import { getMaxQuantity, makeGetDlpOrderValidationErrors } from 'state/secondaryMarket/dlpOrder/dlpOrder.selectors';
import type {
  LimitValidity,
  OrderType,
  SecondaryMarketDlpOrderForm,
} from 'state/secondaryMarket/dlpOrder/form/dlpOrderForm.model';
import {
  getInputFormCustodian,
  getInputFormLimit,
  getInputFormLimitDate,
  getInputFormLimitTime,
  getInputFormOrderType,
  getInputFormQuantity,
  getInputFormTradeWay,
  getInputFormValidity,
} from 'state/secondaryMarket/dlpOrder/form/dlpOrderForm.selectors';
import {
  secondaryMarketDlpOrderFormCustodianChangedThunk,
  secondaryMarketDlpOrderFormLimitChangedThunk,
  secondaryMarketDlpOrderFormLimitDateChangedThunk,
  secondaryMarketDlpOrderFormLimitTimeChangedThunk,
  secondaryMarketDlpOrderFormOrderTypeChangedThunk,
  secondaryMarketDlpOrderFormQuantityChangedThunk,
  secondaryMarketDlpOrderFormTradeWayChangedThunk,
  secondaryMarketDlpOrderFormValidityChangedThunk,
} from 'state/secondaryMarket/dlpOrder/order/order.thunks';
import {
  getAvailableCustodians,
  getDisabledTradeWays,
  getSecondaryMarketDlpProductIsinFromTab,
} from 'state/secondaryMarket/referential/dlpProduct/dlpProduct.selectors';
import { type TradeWay } from 'state/secondaryMarket/secondaryMarket.model';
import { SecondaryMarketFormError } from 'state/secondaryMarket/shared/form/validationResults';
import type { ThunkDispatch } from 'state/state.common';
import type { AppState } from 'state/state.model';
import { getTabIsFrozen } from 'state/workspaces/tabs/tabs.selector';

type OwnProps = Readonly<{
  tabId: string;
}>;

type StateProps = Readonly<{
  isin?: string;
  quantity: number | undefined;
  limit: number | undefined;
  limitDate?: string;
  limitTime?: string;
  tradeWay: TradeWay;
  orderType: OrderType;
  validity?: LimitValidity;
  selectedCustodian: Custodian | undefined;
  availableCustodians: Custodian[];
  disabledTradeWays: readonly TradeWay[];
  isFrozen?: boolean;
  maxQuantity: number;
  validationErrors: SecondaryMarketFormError<SecondaryMarketDlpOrderForm>[];
}>;

type DispatchProps = Readonly<{
  onQuantityChanged: (value: number | undefined) => void;
  onLimitChanged: (value: number | undefined) => void;
  onLimitDateChanged: (value: string | undefined) => void;
  onLimitTimeChanged: (value: string | undefined) => void;
  onOrderTypeChanged: (value: OrderType) => void;
  onValidityChanged: (value: LimitValidity | undefined) => void;
  onTradeWayChanged: (value: TradeWay) => void;
  onCustodianChanged: (value: Custodian | undefined | null) => void;
}>;

export type DlpSecondaryTradeInformationProps = OwnProps & StateProps & DispatchProps;

const mapStateToProps: MapStateToPropsFactory<StateProps, OwnProps, AppState> = () => {
  const getValidationErrors = makeGetDlpOrderValidationErrors();

  return (state, { tabId }) => {
    const isin = getSecondaryMarketDlpProductIsinFromTab(state, tabId);
    const quantity = getInputFormQuantity(state, tabId);
    const limit = getInputFormLimit(state, tabId);
    const limitDate = getInputFormLimitDate(state, tabId);
    const limitTime = getInputFormLimitTime(state, tabId);
    const tradeWay = getInputFormTradeWay(state, tabId);
    const orderType = getInputFormOrderType(state, tabId);
    const validity = getInputFormValidity(state, tabId);
    const selectedCustodian = getInputFormCustodian(state, tabId);
    const availableCustodians = getAvailableCustodians(state, tabId) ?? [];
    const disabledTradeWays = getDisabledTradeWays(state, tabId);
    const maxQuantity = getMaxQuantity(state, tabId);
    const isFrozen = getTabIsFrozen(state, tabId) ?? false;
    const validationErrors = getValidationErrors(state, tabId);

    return {
      isin,
      selectedCustodian,
      quantity,
      tradeWay,
      disabledTradeWays,
      orderType,
      validity,
      limit,
      isFrozen,
      availableCustodians,
      limitDate,
      limitTime,
      maxQuantity,
      validationErrors,
    };
  };
};

const mapDispatchToProps = (dispatch: ThunkDispatch, { tabId }: OwnProps): DispatchProps => {
  return {
    onQuantityChanged: value => {
      dispatch(secondaryMarketDlpOrderFormQuantityChangedThunk(tabId, value));
    },
    onLimitChanged: value => {
      dispatch(secondaryMarketDlpOrderFormLimitChangedThunk(tabId, value));
    },
    onLimitDateChanged: value => {
      dispatch(secondaryMarketDlpOrderFormLimitDateChangedThunk(tabId, value));
    },
    onLimitTimeChanged: value => {
      dispatch(secondaryMarketDlpOrderFormLimitTimeChangedThunk(tabId, value));
    },
    onTradeWayChanged: value => {
      dispatch(secondaryMarketDlpOrderFormTradeWayChangedThunk(tabId, value));
    },
    onValidityChanged: value => {
      dispatch(secondaryMarketDlpOrderFormValidityChangedThunk(tabId, value));
    },
    onOrderTypeChanged: value => {
      dispatch(secondaryMarketDlpOrderFormOrderTypeChangedThunk(tabId, value));
    },
    onCustodianChanged: value => {
      dispatch(secondaryMarketDlpOrderFormCustodianChangedThunk(tabId, value));
    },
  };
};

export const connectDlpSecondaryTradeInformation = connect(mapStateToProps, mapDispatchToProps);
